/**
 * ProductAddons — clickable add-on chips grouped by category.
 *
 * Each addon has: {key, name, category, price, mode: "flat"|"per_unit", description?}
 * Clicking toggles the addon. The parent component watches selectedKeys and
 * passes them to the pricing engine via /api/products/price-calc.
 */
import { useMemo } from "react";
import { Check, Plus } from "lucide-react";
import { money } from "../lib/api";

export default function ProductAddons({ addons, selectedKeys, onToggle, quantity = 1 }) {
  // Group by category, preserve first-seen order.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of addons || []) {
      const cat = a.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(a);
    }
    return Array.from(map.entries());
  }, [addons]);

  if (!grouped.length) return null;

  return (
    <div className="space-y-4" data-testid="product-addons">
      <div className="overline">Add-ons &amp; Upgrades</div>
      {grouped.map(([category, items]) => (
        <div key={category} data-testid={`addon-category-${category.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 font-bold mb-2">
            {category}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((a) => {
              const selected = selectedKeys.includes(a.key);
              const effectivePrice = a.mode === "per_unit" ? a.price * quantity : a.price;
              const isFree = (Number(a.price) || 0) === 0;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => onToggle(a.key)}
                  className={`text-left border-2 p-3 transition-all ${
                    selected
                      ? "border-kolt-orange bg-orange-50"
                      : "border-slate-200 bg-white hover:border-kolt-slate"
                  }`}
                  data-testid={`addon-${a.key}`}
                  aria-pressed={selected}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-kolt-slate-dark">{a.name}</span>
                        {isFree && (
                          <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-mono tracking-widest font-bold uppercase">
                            Free
                          </span>
                        )}
                      </div>
                      {a.description && (
                        <p className="text-xs text-slate-500 mt-1 leading-snug">{a.description}</p>
                      )}
                      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-1.5">
                        {isFree ? "Included" : (
                          <>
                            {money(effectivePrice)}
                            {a.mode === "per_unit" && (
                              <span className="text-slate-400 normal-case tracking-normal ml-1">
                                · {money(a.price)} × {quantity}
                              </span>
                            )}
                            {a.mode === "flat" && (
                              <span className="text-slate-400 normal-case tracking-normal ml-1">
                                · flat
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      className={`flex-shrink-0 w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                        selected
                          ? "border-kolt-orange bg-kolt-orange text-white"
                          : "border-slate-300 bg-white text-slate-300"
                      }`}
                      aria-hidden
                    >
                      {selected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
